import React from "react";
import { motion } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { BUSINESS, waLink, defaultWaMessage } from "@/lib/config";

const stats = [
  { value: "500+", label: "Products" },
  { value: "20+", label: "Trusted Brands" },
  { value: "15+", label: "Years Trust" },
];

export default function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100svh] flex items-center overflow-hidden pt-20"
    >
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_mastermall-showroom/artifacts/iernou36_WhatsApp%20Image%202026-07-02%20at%2012.11.56%20AM.jpeg"
          alt="Master Mall Nashik showroom building"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </div>
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#00296B] via-[#00296B]/90 to-[#00296B]/60" />
      <div className="mm-grain" />

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="lg:col-span-8 text-white"
          >
            <div className="inline-flex items-center gap-2 bg-[#FFD500]/15 border border-[#FFD500]/40 text-[#FFD500] px-4 py-1.5 mb-6 text-xs tracking-[0.2em] uppercase font-semibold">
              <span className="w-1.5 h-1.5 bg-[#FFD500]" /> Nashik&apos;s Premier Electrical Showroom
            </div>

            <h1
              className="font-display font-bold text-white text-4xl sm:text-5xl lg:text-6xl xl:text-7xl leading-[1.05] tracking-tight"
              data-testid="hero-headline"
            >
              Complete{" "}
              <span className="relative inline-block isolate">
                <span
                  aria-hidden
                  className="absolute left-0 right-0 bottom-0 h-2 sm:h-3 bg-[#FFD500] -z-10"
                />
                <span className="relative">Electrical</span>
              </span>{" "}
              Solutions
              <br className="hidden sm:block" /> Under One Roof
            </h1>

            <p
              className="mt-6 text-base sm:text-lg lg:text-xl text-white/85 max-w-2xl leading-relaxed font-normal"
              data-testid="hero-subheading"
            >
              {BUSINESS.name} offers premium electrical products from trusted
              brands for homes, commercial projects and industries.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a
                href={waLink(defaultWaMessage)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-whatsapp-btn"
                className="group inline-flex items-center justify-center gap-3 bg-[#FFD500] text-[#00296B] font-semibold px-8 py-4 hover:bg-[#E6C000] transition-all"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden
                >
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.966-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.297-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Chat on WhatsApp
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>

              <a
                href={`tel:${BUSINESS.phoneRaw}`}
                data-testid="hero-call-btn"
                className="group inline-flex items-center justify-center gap-3 bg-transparent border-2 border-white text-white font-semibold px-8 py-4 hover:bg-white hover:text-[#00296B] transition-all"
              >
                <Phone className="w-5 h-5" />
                Call Now
              </a>
            </div>

            <div
              className="mt-14 grid grid-cols-3 gap-6 sm:gap-10 max-w-xl border-t border-white/20 pt-8"
              data-testid="hero-stats"
            >
              {stats.map((s) => (
                <div key={s.label}>
                  <div className="font-display font-bold text-3xl sm:text-4xl text-[#FFD500]">
                    {s.value}
                  </div>
                  <div className="text-xs sm:text-sm text-white/70 tracking-wider uppercase mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Side card */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            className="hidden lg:block lg:col-span-4"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 p-8 relative">
              <div className="absolute -top-3 left-8 bg-[#FFD500] text-[#00296B] text-xs font-bold px-3 py-1 tracking-widest">
                VISIT US
              </div>
              <div className="text-white/70 text-xs uppercase tracking-widest mb-3 mt-2">
                Showroom Hours
              </div>
              {BUSINESS.hours.map((h) => (
                <div
                  key={h.day}
                  className="flex justify-between py-3 border-b border-white/15 last:border-0"
                >
                  <span className="text-white/85 text-sm">{h.day}</span>
                  <span className="text-[#FFD500] font-semibold text-sm">
                    {h.time}
                  </span>
                </div>
              ))}
              <div className="mt-6 pt-4 border-t border-white/15">
                <div className="text-white/70 text-xs uppercase tracking-widest mb-2">
                  Location
                </div>
                <div className="text-white text-sm leading-relaxed">
                  {BUSINESS.address.line1}
                  <br />
                  {BUSINESS.address.line2}
                  <br />
                  {BUSINESS.address.line3}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-1 text-white/60 text-xs tracking-[0.3em]">
        SCROLL
        <div className="w-px h-8 bg-gradient-to-b from-white/60 to-transparent" />
      </div>
    </section>
  );
}
